// ✅ src/pages/LoginPage.js (FULL - UPDATED: NO MOBILE INPUT ZOOM)
// ✅ Fix: iPhone/Safari zoom-on-focus stopped by setting input fontSize >= 16
// ✅ Keeps: referral creation + email verification gate

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { auth, db } from "../lib/firebase";
import {
  loginWithGoogle,
  loginWithEmail,
  signUpWithEmail,
  resetPassword,
  resendVerificationEmail,
  refreshUser,
} from "../lib/auth";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

export default function LoginPage({ onSuccess, goBack }) {
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");

  // ✅ referral code from URL / localStorage
  const [refCode, setRefCode] = useState("");

  // ✅ verification UI
  const [needsVerify, setNeedsVerify] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");

  // ✅ show any message passed from guards (like "verify your email")
  useEffect(() => {
    const m = safe(location.state?.msg);
    if (m) setMsg(m);
  }, [location.state]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const ref = safe(sp.get("ref"));

    if (ref) {
      setRefCode(ref);
      try {
        localStorage.setItem("pendingReferralCode", ref);
      } catch {}
    } else {
      try {
        const saved = safe(localStorage.getItem("pendingReferralCode"));
        if (saved) setRefCode(saved);
      } catch {}
    }
  }, [location.search]);

  const tryCreateReferral = async () => {
    try {
      const code =
        safe(refCode) || safe(localStorage.getItem("pendingReferralCode"));
      if (!code) return;

      const u = auth.currentUser;
      if (!u?.uid) return;

      const newUid = u.uid;

      const refUserQ = query(
        collection(db, "users"),
        where("referralCode", "==", code),
        limit(1)
      );
      const refUserSnap = await getDocs(refUserQ);
      if (refUserSnap.empty) return;

      const referrerUid = refUserSnap.docs[0].id;

      if (referrerUid === newUid) return;

      const dupQ = query(
        collection(db, "referrals"),
        where("referrerUid", "==", referrerUid),
        where("referredUid", "==", newUid),
        limit(1)
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        try {
          localStorage.removeItem("pendingReferralCode");
        } catch {}
        setRefCode("");
        return;
      }

      await addDoc(collection(db, "referrals"), {
        referrerUid,
        referredUid: newUid,
        rewardAmount: 7,
        currency: "GHS",
        status: "PENDING",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        qualifiedAt: null,
        rewardedAt: null,
      });

      try {
        localStorage.removeItem("pendingReferralCode");
      } catch {}
      setRefCode("");
    } catch (e) {
      console.error("[LOGIN][REF] create referral failed:", e);
    }
  };

  const styles = useMemo(
    () => ({
      page: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        overflow: "hidden",
        position: "relative",
        background:
          "linear-gradient(135deg, #0b0620 0%, #1a1035 25%, #0b0620 50%, #1a1035 75%, #0b0620 100%)",
      },
      backgroundArt: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: "hidden",
      },
      particle: {
        position: "absolute",
        background: "rgba(255,255,255,0.1)",
        borderRadius: "50%",
        animation: "twinkle 4s ease-in-out infinite",
      },
      card: {
        width: "100%",
        maxWidth: 440,
        padding: 32,
        borderRadius: 24,
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.15)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        color: "#fff",
        position: "relative",
        zIndex: 1,
      },
      title: { fontSize: 28, fontWeight: 800, marginBottom: 8 },
      subtitle: {
        fontSize: 14,
        color: "rgba(255,255,255,0.7)",
        marginBottom: 18,
      },
      hint: {
        fontSize: 12,
        color: "rgba(255,255,255,0.75)",
        marginBottom: 12,
        padding: "10px 12px",
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.15)",
        background: "rgba(0,0,0,0.18)",
      },
      googleBtn: {
        width: "100%",
        padding: 16,
        borderRadius: 16,
        border: "none",
        background: "linear-gradient(135deg, #4285f4, #1a73e8)",
        color: "#fff",
        fontWeight: 700,
        cursor: "pointer",
        marginBottom: 16,
      },
      divider: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 13,
        color: "rgba(255,255,255,0.6)",
        margin: "20px 0",
      },
      line: { flex: 1, height: 1, background: "rgba(255,255,255,0.3)" },

      // ✅ KEY FIX: fontSize 16 stops iPhone/Safari zoom-on-focus
      input: {
        width: "100%",
        padding: 16,
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.15)",
        background: "rgba(0,0,0,0.25)",
        color: "#fff",
        outline: "none",
        marginBottom: 12,
        fontSize: 16,
        WebkitTextSizeAdjust: "100%",
      },

      forgotRow: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: -4,
        marginBottom: 16,
      },
      linkBtn: {
        border: "none",
        background: "transparent",
        color: "rgba(255,255,255,0.85)",
        cursor: "pointer",
        fontSize: 13,
        textDecoration: "underline",
        padding: 0,
      },
      btnRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
      loginBtn: {
        padding: 16,
        borderRadius: 16,
        border: "none",
        background: "linear-gradient(135deg, #ff4ecd, #9b4dff)",
        color: "#fff",
        fontWeight: 800,
        cursor: "pointer",
      },
      signupBtn: {
        padding: 16,
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.2)",
        background: "rgba(255,255,255,0.12)",
        color: "#fff",
        fontWeight: 800,
        cursor: "pointer",
      },
      legal: {
        marginTop: 12,
        fontSize: 12,
        color: "rgba(255,255,255,0.65)",
        lineHeight: 1.4,
      },
      legalLink: { color: "rgba(255,255,255,0.9)", textDecoration: "underline" },
      backBtn: {
        width: "100%",
        marginTop: 20,
        padding: 14,
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.15)",
        background: "rgba(0,0,0,0.25)",
        color: "#fff",
        cursor: "pointer",
        fontWeight: 700,
      },
      error: {
        marginTop: 20,
        padding: 16,
        borderRadius: 16,
        background: "rgba(255, 60, 120, 0.15)",
        border: "1px solid rgba(255, 60, 120, 0.35)",
        fontSize: 13,
        color: "#ff8ba0",
      },
      verifyBox: {
        marginTop: 14,
        padding: 14,
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.2)",
        background: "rgba(0,0,0,0.2)",
      },
      verifyTitle: { fontWeight: 800, marginBottom: 8 },
      small: {
        fontSize: 12,
        color: "rgba(255,255,255,0.75)",
        lineHeight: 1.4,
      },
      verifyRow: {
        display: "flex",
        gap: 10,
        marginTop: 12,
        flexWrap: "wrap",
      },
      verifyBtn: {
        flex: 1,
        minWidth: 140,
        padding: 12,
        borderRadius: 14,
        border: "none",
        background: "linear-gradient(135deg, #22c55e, #16a34a)",
        color: "#fff",
        fontWeight: 800,
        cursor: "pointer",
      },
      verifyBtn2: {
        flex: 1,
        minWidth: 140,
        padding: 12,
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.2)",
        background: "rgba(255,255,255,0.12)",
        color: "#fff",
        fontWeight: 800,
        cursor: "pointer",
      },
    }),
    []
  );

  // ✅ gate: only allow verified email users to continue (Google users are already verified)
  const finishAuth = async () => {
    await tryCreateReferral();

    const u = auth.currentUser;
    if (!u) throw new Error("No user");

    const usesPassword = (u.providerData || []).some(
      (p) => p?.providerId === "password"
    );

    if (usesPassword && !u.emailVerified) {
      setNeedsVerify(true);
      setVerifyEmail(u.email || "");
      return;
    }

    setNeedsVerify(false);
    await onSuccess();
  };

  return (
    <div style={styles.page}>
      <div style={styles.backgroundArt}>
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.particle,
              width: Math.random() * 4 + 2,
              height: Math.random() * 4 + 2,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div style={styles.card}>
        <h2 style={styles.title}>Join Lirivelle</h2>
        <p style={styles.subtitle}>
          Buy and sell digital items securely. Please log in or create an
          account.
        </p>

        {refCode ? (
          <div style={styles.hint}>
            Referral detected ✅ <b>{refCode}</b>
            <br />
            (We’ll link the referral after you sign up / login.)
          </div>
        ) : null}

        <button
          style={styles.googleBtn}
          onClick={async () => {
            try {
              setMsg("");
              setNeedsVerify(false);

              const user = await loginWithGoogle();

              if (user) {
                await finishAuth();
              } else {
                setMsg("Redirecting to Google...");
              }
            } catch (e) {
              setMsg(e?.message || "Google login failed");
            }
          }}
        >
          Continue with Google
        </button>

        <div style={styles.divider}>
          <div style={styles.line} />
          <span>or use email</span>
          <div style={styles.line} />
        </div>

        <input
          style={styles.input}
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          style={styles.input}
          placeholder="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div style={styles.forgotRow}>
          <button
            type="button"
            style={styles.linkBtn}
            onClick={async () => {
              if (!email.trim()) {
                setMsg("Enter your email first, then tap “Forgot password?”");
                return;
              }
              try {
                setMsg("");
                await resetPassword(email.trim());
                setMsg("Password reset email sent. Check your inbox (and spam).");
              } catch (e) {
                setMsg(e?.message || "Failed to send reset email");
              }
            }}
          >
            Forgot password?
          </button>
        </div>

        <div style={styles.btnRow}>
          <button
            style={styles.loginBtn}
            onClick={async () => {
              try {
                setMsg("");
                setNeedsVerify(false);

                await loginWithEmail(email, password);
                await finishAuth();
              } catch (e) {
                setMsg(e?.message || "Login failed");
              }
            }}
          >
            Login
          </button>

          <button
            style={styles.signupBtn}
            onClick={async () => {
              try {
                setMsg("");
                setNeedsVerify(false);

                await signUpWithEmail(email, password);
                await finishAuth();
              } catch (e) {
                setMsg(e?.message || "Signup failed");
              }
            }}
          >
            Sign Up
          </button>
        </div>

        {/* ✅ Email verification gate */}
        {needsVerify ? (
          <div style={styles.verifyBox}>
            <div style={styles.verifyTitle}>Verify your email ✅</div>
            <div style={styles.small}>
              We sent a verification email to <b>{verifyEmail || "your inbox"}</b>
              .
              <br />
              Open it and tap the verification link, then come back and press “I
              verified”.
            </div>

            <div style={styles.verifyRow}>
              <button
                style={styles.verifyBtn2}
                onClick={async () => {
                  try {
                    setMsg("");
                    await resendVerificationEmail();
                    setMsg("Verification email resent. Check inbox/spam.");
                  } catch (e) {
                    setMsg(e?.message || "Failed to resend verification email");
                  }
                }}
              >
                Resend email
              </button>

              <button
                style={styles.verifyBtn}
                onClick={async () => {
                  try {
                    setMsg("");
                    await refreshUser();
                    const u = auth.currentUser;

                    if (u?.emailVerified) {
                      setNeedsVerify(false);
                      await onSuccess();
                    } else {
                      setMsg(
                        "Still not verified yet. Verify, then tap “I verified” again."
                      );
                    }
                  } catch (e) {
                    setMsg(e?.message || "Could not refresh verification status");
                  }
                }}
              >
                I verified
              </button>
            </div>
          </div>
        ) : null}

        <div style={styles.legal}>
          By signing up, you agree to our{" "}
          <Link to="/terms" style={styles.legalLink}>
            Terms
          </Link>{" "}
          and{" "}
          <Link to="/privacy" style={styles.legalLink}>
            Privacy Policy
          </Link>
          .
        </div>

        <button style={styles.backBtn} onClick={goBack}>
          ← Back
        </button>

        {msg && <div style={styles.error}>{msg}</div>}
      </div>
    </div>
  );
}
